import type { ElementCategory, ElementRef } from "../model/inp/properties.ts";
import type { SimulationResults } from "../model/results/simulationResults.ts";
import { useT } from "../i18n/hooks.ts";
import { ResultsDashboardCategoryBlock } from "./ResultsDashboardCategoryBlock.tsx";
import type { ResultsDashboardLayout, ResultsDashboardView } from "./useResultsDashboard.ts";
import { CloseIcon } from "./CloseIcon.tsx";
import { PlusIcon } from "./PlusIcon.tsx";
import { TrashIcon } from "./TrashIcon.tsx";
import { LayoutGridIcon } from "./LayoutGridIcon.tsx";
import { ResultsDashboardCombinedIcon } from "./ResultsDashboardCombinedIcon.tsx";
import { ResultsDashboardChartIcon } from "./ResultsDashboardChartIcon.tsx";
import { ResultsDashboardTableIcon } from "./ResultsDashboardTableIcon.tsx";

/** Category blocks always render in this order, regardless of the order elements were added in. */
const CATEGORY_ORDER: ElementCategory[] = ["raingage", "subcatchment", "node", "link"];

/**
 * Docked in `.workspace-body` alongside PropertyPanel (App.tsx renders
 * exactly one of the two) rather than a modal — a modal's backdrop would
 * block map clicks, making it impossible to build a multi-element
 * selection while this panel is open.
 *
 * `elements` is its own separate selection set (useResultsDashboard.ts),
 * not App.tsx's `selected`/`multiSelected` — the map/tree keep responding
 * to clicks normally while this is open, and "Add selection" is what
 * copies the current selection into this set.
 */
export function ResultsDashboardPanel({
  results,
  elements,
  selected,
  multiSelected,
  flowUnits,
  layout,
  view,
  onAddSelection,
  onRemoveElement,
  onClear,
  onLayoutChange,
  onViewChange,
  onClose,
}: {
  results: SimulationResults;
  elements: ElementRef[];
  selected: ElementRef | null;
  multiSelected: ElementRef[];
  flowUnits: string;
  layout: ResultsDashboardLayout;
  view: ResultsDashboardView;
  onAddSelection: () => void;
  onRemoveElement: (ref: ElementRef) => void;
  onClear: () => void;
  onLayoutChange: (layout: ResultsDashboardLayout) => void;
  onViewChange: (view: ResultsDashboardView) => void;
  onClose: () => void;
}) {
  const t = useT();
  const canAdd = multiSelected.length > 0 || selected !== null;

  const byCategory = new Map<ElementCategory, ElementRef[]>();
  for (const el of elements) {
    if (!byCategory.has(el.category)) byCategory.set(el.category, []);
    byCategory.get(el.category)!.push(el);
  }

  return (
    <section className="results-dashboard-panel">
      <div className="results-dashboard-header">
        <h2>{t("resultsDashboard.title")}</h2>
        <button className="button-icon" onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon />
        </button>
      </div>
      <div className="results-dashboard-toolbar">
        <button className="button" onClick={onAddSelection} disabled={!canAdd}>
          <PlusIcon />
          {t("resultsDashboard.addSelection")}
        </button>
        <button className="button" onClick={onClear} disabled={elements.length === 0}>
          <TrashIcon />
          {t("resultsDashboard.clear")}
        </button>
        <span className="topbar-spacer" />
        <div className="results-chart-view-toggle" role="tablist">
          <button
            className={layout === "combined" ? "button tab-active" : "button"}
            role="tab"
            aria-selected={layout === "combined"}
            onClick={() => onLayoutChange("combined")}
          >
            <ResultsDashboardCombinedIcon />
            {t("resultsDashboard.layoutCombined")}
          </button>
          <button
            className={layout === "grid" ? "button tab-active" : "button"}
            role="tab"
            aria-selected={layout === "grid"}
            onClick={() => onLayoutChange("grid")}
          >
            <LayoutGridIcon />
            {t("resultsDashboard.layoutGrid")}
          </button>
        </div>
        <div className="results-chart-view-toggle" role="tablist">
          <button
            className={view === "chart" ? "button tab-active" : "button"}
            role="tab"
            aria-selected={view === "chart"}
            onClick={() => onViewChange("chart")}
          >
            <ResultsDashboardChartIcon />
            {t("results.viewChart")}
          </button>
          <button
            className={view === "table" ? "button tab-active" : "button"}
            role="tab"
            aria-selected={view === "table"}
            onClick={() => onViewChange("table")}
          >
            <ResultsDashboardTableIcon />
            {t("results.viewTable")}
          </button>
        </div>
      </div>
      {elements.length === 0 && (
        <p className="results-dashboard-hint">{t("resultsDashboard.empty")}</p>
      )}
      {CATEGORY_ORDER.map((category) => {
        const categoryElements = byCategory.get(category);
        if (!categoryElements) return null;
        return (
          <ResultsDashboardCategoryBlock
            key={category}
            category={category}
            elements={categoryElements}
            results={results}
            flowUnits={flowUnits}
            layout={layout}
            view={view}
            onRemoveElement={onRemoveElement}
          />
        );
      })}
    </section>
  );
}
